if (!customElements.get('sey-device-finder')) {
  customElements.define(
    'sey-device-finder',
    class SeyDeviceFinder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.data = this.parseData();
        this.selectedBrandId = '';
        this.selectedModelId = '';
        this.applyCurrentCollection();
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        this.brandField = this.querySelector('[data-field="brand"]');
        this.modelField = this.querySelector('[data-field="model"]');
        this.form = this.querySelector('.sey-device-finder__form');
        this.error = this.querySelector('[data-error]');
        this.mobileTabs = this.querySelector('[data-brand-tabs]');
        this.mobileModels = this.querySelector('[data-model-list]');
        this.mobileEmpty = this.querySelector('[data-mobile-empty]');
        this.mobileOverlay = this.querySelector('[data-mobile-overlay]');
        this.mobileBackdrop = this.querySelector('[data-mobile-backdrop]');

        this.bindField(this.brandField);
        this.bindField(this.modelField);
        this.form.addEventListener('submit', this.onSubmit);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick);
        this.mobileBackdrop?.addEventListener('click', () => this.closeSheet());

        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.onViewportChange = this.onViewportChange.bind(this);
        this.mobileQuery.addEventListener('change', this.onViewportChange);

        this.renderBrandOptions();
        this.renderModelOptions();
        this.renderMobile();
        this.updateTriggers();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        this.mobileQuery?.removeEventListener('change', this.onViewportChange);
        this.closeSheet();
      }

      isMobile() {
        return this.mobileQuery?.matches ?? window.matchMedia('(max-width: 749px)').matches;
      }

      onViewportChange() {
        if (!this.isMobile()) this.closeSheet();
      }

      parseData() {
        const script = this.querySelector('[data-sey-device-finder-data]');
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return { brands: [], i18n: {} };
        }
      }

      bindField(field) {
        const trigger = field.querySelector('[data-trigger]');
        const filter = field.querySelector('[data-filter]');

        trigger.addEventListener('click', () => {
          if (this.isMobile()) {
            this.openSheet();
            return;
          }
          if (trigger.getAttribute('aria-disabled') === 'true') return;
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          this.closeAll();
          if (!isOpen) this.openField(field);
        });

        filter.addEventListener('input', () => this.filterOptions(field));
        filter.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') event.preventDefault();
        });
      }

      openField(field) {
        const trigger = field.querySelector('[data-trigger]');
        const panel = field.querySelector('[data-panel]');
        const filter = field.querySelector('[data-filter]');
        trigger.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        filter.value = '';
        this.filterOptions(field);
        filter.focus();
      }

      closeField(field) {
        const trigger = field.querySelector('[data-trigger]');
        const panel = field.querySelector('[data-panel]');
        trigger.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
      }

      closeAll() {
        this.closeField(this.brandField);
        this.closeField(this.modelField);
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.closeAll();
      }

      onKeydown(event) {
        if (event.key !== 'Escape') return;
        if (this.mobileOverlay?.classList.contains('is-open')) {
          this.closeSheet();
        } else {
          this.closeAll();
        }
        event.stopPropagation();
      }

      renderBrandOptions() {
        const options = this.brandField.querySelector('[data-options]');
        options.replaceChildren();
        this.data.brands.forEach((brand) => {
          options.appendChild(
            this.createOption(brand, 'brand', String(brand.id) === String(this.selectedBrandId))
          );
        });
        this.syncEmptyState(this.brandField, this.data.brands.length === 0, this.data.i18n.noResults);
      }

      renderModelOptions() {
        const options = this.modelField.querySelector('[data-options]');
        const models = this.getSelectedBrand()?.models || [];
        options.replaceChildren();
        models.forEach((model) => {
          options.appendChild(
            this.createOption(model, 'model', String(model.id) === String(this.selectedModelId), { hideLogo: true })
          );
        });

        const noBrand = !this.selectedBrandId;
        this.syncEmptyState(
          this.modelField,
          models.length === 0,
          noBrand ? this.data.i18n.noModels : this.data.i18n.noResults
        );

        const trigger = this.modelField.querySelector('[data-trigger]');
        const disabled = !this.selectedBrandId && !this.isMobile();
        trigger.classList.toggle('is-disabled', disabled);
        trigger.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        if (disabled) this.closeField(this.modelField);
      }

      createOption(item, type, selected, flags = {}) {
        const option = document.createElement('li');
        option.className = 'sey-device-finder__option';
        option.dataset.label = String(item.title || '').toLowerCase();

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sey-device-finder__option-button';
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) button.classList.add('is-selected');

        if (!flags.hideLogo) {
          if (item.logoUrl) {
            const img = document.createElement('img');
            img.className = 'sey-device-finder__logo';
            img.src = item.logoUrl;
            img.alt = '';
            img.width = 24;
            img.height = 24;
            button.appendChild(img);
          } else {
            button.appendChild(this.createLogoFallback(item.title));
          }
        }

        const text = document.createElement('span');
        text.className = 'sey-device-finder__option-text';
        text.textContent = item.title;
        button.appendChild(text);

        button.addEventListener('click', () => this.selectOption(type, String(item.id)));
        option.appendChild(button);
        return option;
      }

      createLogoFallback(title) {
        const mark = document.createElement('span');
        mark.className = 'sey-device-finder__logo-fallback';
        mark.setAttribute('aria-hidden', 'true');
        mark.textContent = String(title || '').trim().charAt(0).toUpperCase();
        return mark;
      }

      selectOption(type, id) {
        if (type === 'brand') {
          this.selectedBrandId = id;
          this.selectedModelId = '';
          this.renderBrandOptions();
          this.renderModelOptions();
          this.closeField(this.brandField);
        } else {
          this.selectedModelId = id;
          this.renderModelOptions();
          this.closeField(this.modelField);
        }
        this.renderMobile();
        this.updateTriggers();
        this.hideError();

        if (type === 'model' && this.hasAttribute('data-auto-navigate')) {
          const model = this.getSelectedModel();
          if (model?.url && !this.isCurrentModel(model)) {
            window.location.href = model.url;
          }
        }
      }

      applyCurrentCollection() {
        const current = this.data.currentCollection || {};
        const handle = String(current.handle || '').trim();
        const url = String(current.url || '').trim();
        if (!handle && !url) return;

        for (const brand of this.data.brands || []) {
          for (const model of brand.models || []) {
            if (this.modelMatchesCollection(model, handle, url)) {
              this.selectedBrandId = String(brand.id);
              this.selectedModelId = String(model.id);
              return;
            }
          }
        }
      }

      isCurrentModel(model) {
        const current = this.data.currentCollection || {};
        return this.modelMatchesCollection(
          model,
          String(current.handle || '').trim(),
          String(current.url || '').trim()
        );
      }

      modelMatchesCollection(model, handle, url) {
        if (handle && model.handle && String(model.handle) === handle) return true;
        return this.sameCollectionPath(model.url, url);
      }

      sameCollectionPath(a, b) {
        if (!a || !b) return false;
        const normalize = (value) => {
          try {
            return new URL(value, window.location.origin).pathname.replace(/\/+$/, '').toLowerCase();
          } catch (error) {
            return String(value).replace(/\/+$/, '').toLowerCase();
          }
        };
        return normalize(a) === normalize(b);
      }

      filterOptions(field) {
        const query = field.querySelector('[data-filter]').value.trim().toLowerCase();
        const items = Array.from(field.querySelectorAll('.sey-device-finder__option'));
        let visibleCount = 0;
        items.forEach((item) => {
          const match = !query || item.dataset.label.includes(query);
          item.hidden = !match;
          if (match) visibleCount += 1;
        });
        const emptyByFilter = items.length > 0 && visibleCount === 0;
        if (emptyByFilter) this.syncEmptyState(field, true, this.data.i18n.noResults);
        else if (items.length > 0) field.querySelector('[data-empty]').hidden = true;
      }

      syncEmptyState(field, isEmpty, message) {
        const empty = field.querySelector('[data-empty]');
        empty.textContent = message;
        empty.hidden = !isEmpty;
      }

      openSheet() {
        if (!this.mobileOverlay) return;
        if (!this.selectedBrandId && this.data.brands[0]) {
          this.selectedBrandId = String(this.data.brands[0].id);
          this.renderBrandOptions();
          this.renderModelOptions();
          this.updateTriggers();
        }
        this.renderMobile();
        this.mobileOverlay.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => {
          this.mobileOverlay.classList.add('is-open');
          this.scrollSelectedModelIntoView();
        });
        document.body.classList.add('overflow-hidden-mobile');
        this.brandField.querySelector('[data-trigger]')?.setAttribute('aria-expanded', 'true');
        this.modelField.querySelector('[data-trigger]')?.setAttribute('aria-expanded', 'true');
      }

      closeSheet() {
        if (!this.mobileOverlay?.classList.contains('is-open')) return;
        this.mobileOverlay.classList.remove('is-open');
        this.mobileOverlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden-mobile');
        this.brandField.querySelector('[data-trigger]')?.setAttribute('aria-expanded', 'false');
        this.modelField.querySelector('[data-trigger]')?.setAttribute('aria-expanded', 'false');
      }

      renderMobile() {
        if (!this.mobileTabs || !this.mobileModels) return;
        this.renderMobileBrands();
        this.renderMobileModels();
      }

      renderMobileBrands() {
        this.mobileTabs.replaceChildren();
        this.data.brands.forEach((brand) => {
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'sey-device-finder__tab';
          tab.setAttribute('role', 'tab');
          const selected = String(brand.id) === String(this.selectedBrandId);
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          if (selected) tab.classList.add('is-active');
          tab.textContent = brand.title;
          tab.addEventListener('click', () => this.selectOption('brand', String(brand.id)));
          this.mobileTabs.appendChild(tab);
        });
      }

      renderMobileModels() {
        const models = this.getSelectedBrand()?.models || [];
        this.mobileModels.replaceChildren();
        models.forEach((model) => {
          if (!model?.url) return;
          const item = document.createElement('li');
          item.className = 'sey-device-finder__model';
          const link = document.createElement('a');
          link.className = 'sey-device-finder__model-link';
          if (String(model.id) === String(this.selectedModelId)) {
            link.classList.add('is-selected');
          }
          link.href = model.url;
          if (this.isCurrentModel(model)) {
            link.addEventListener('click', (event) => {
              event.preventDefault();
              this.closeSheet();
            });
          }
          link.textContent = model.title;
          item.appendChild(link);
          this.mobileModels.appendChild(item);
        });

        const noBrand = !this.selectedBrandId;
        const isEmpty = this.mobileModels.children.length === 0;
        this.mobileEmpty.textContent = noBrand ? this.data.i18n.noModels : this.data.i18n.noResults;
        this.mobileEmpty.hidden = !isEmpty;
        this.scrollSelectedModelIntoView();
      }

      scrollSelectedModelIntoView() {
        if (!this.mobileOverlay?.classList.contains('is-open')) return;
        this.mobileModels?.querySelector('.is-selected')?.scrollIntoView({
          block: 'nearest',
        });
      }

      getSelectedBrand() {
        return this.data.brands.find((brand) => String(brand.id) === String(this.selectedBrandId));
      }

      getSelectedModel() {
        return (this.getSelectedBrand()?.models || []).find(
          (model) => String(model.id) === String(this.selectedModelId)
        );
      }

      updateTriggers() {
        const brand = this.getSelectedBrand();
        this.updateTrigger(
          this.brandField,
          brand ? brand.title : this.data.i18n.brandPlaceholder,
          !brand,
          brand?.logoUrl,
          brand?.title
        );
        const model = this.getSelectedModel();
        this.updateTrigger(
          this.modelField,
          model ? model.title : this.data.i18n.modelPlaceholder,
          !model
        );
      }

      updateTrigger(field, label, isPlaceholder, logoUrl, logoTitle) {
        const value = field.querySelector('[data-value]');
        const logo = field.querySelector('[data-logo]');
        const fallback = field.querySelector('[data-logo-fallback]');
        value.textContent = label;
        value.classList.toggle('is-placeholder', isPlaceholder);

        if (logo) {
          if (logoUrl) {
            logo.hidden = false;
            logo.src = logoUrl;
            logo.alt = logoTitle || '';
          } else {
            logo.removeAttribute('src');
            logo.hidden = true;
            logo.alt = '';
          }
        }

        if (fallback) {
          if (!isPlaceholder && !logoUrl) {
            fallback.hidden = false;
            fallback.textContent = String(label || '').trim().charAt(0).toUpperCase();
          } else {
            fallback.hidden = true;
            fallback.textContent = '';
          }
        }
      }

      onSubmit(event) {
        event.preventDefault();
        const model = this.getSelectedModel();
        if (!this.selectedBrandId || !model?.url) {
          this.error.textContent = this.data.i18n.errorRequired;
          this.error.hidden = false;
          return;
        }
        this.hideError();
        window.location.href = model.url;
      }

      hideError() {
        this.error.hidden = true;
        this.error.textContent = '';
      }
    }
  );
}
